import { WarningRegular } from "@fluentui/react-icons";
import type { DocumentDetail } from "../../../services/docuparserApi";
import { formatDate } from "../../../utils";
import { getStatusClassKey, STATUS_CLASSNAME } from "../../../constants";
import styles from "./DetailView.module.css";

interface DetailMetadataProps {
  readonly doc: DocumentDetail | null;
  readonly isLoading: boolean;
  readonly error: string;
  readonly isOpeningInWord: boolean;
  readonly isOpenButtonEnabled: boolean;
  readonly onOpen: () => void;
  readonly onBack: () => void;
}

export function DetailMetadata({
  doc,
  isLoading,
  error,
  isOpeningInWord,
  isOpenButtonEnabled,
  onOpen,
  onBack,
}: DetailMetadataProps) {
  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <span className={styles.spinnerLarge} aria-hidden="true" />
        <p className={styles.loadingText}>Loading document details...</p>
      </div>
    );
  }

  if (error && !doc) {
    return (
      <div className={styles.emptyState}>
        <WarningRegular className={styles.emptyIconError} aria-hidden="true" />
        <h3 className={styles.emptyTitle}>Error</h3>
        <p className={styles.emptyMessage}>{error}</p>
        <button type="button" className={styles.btn} onClick={onBack}>
          Back to Documents
        </button>
      </div>
    );
  }

  const statusClass = doc ? styles[STATUS_CLASSNAME[getStatusClassKey(doc.status)]] : undefined;

  return (
    <div className={styles.detailForm}>
      <div className={styles.formGroup}>
        <span className={styles.formLabel}>Name</span>
        <div className={styles.fieldValue}>{doc?.name || ""}</div>
      </div>
      <div className={styles.formGroup}>
        <span className={styles.formLabel}>Description</span>
        <div className={styles.fieldValue}>{doc?.description || ""}</div>
      </div>
      <div className={styles.formGroup}>
        <span className={styles.formLabel}>Uploaded by &amp; at</span>
        <div className={styles.fieldValueMuted}>
          {doc?.createdBy || "N/A"} on {formatDate(doc?.createdAt || "")}
        </div>
      </div>
      <div className={styles.formGroup}>
        <span className={styles.formLabel}>Status</span>
        <span className={`${styles.statusBadge} ${statusClass ?? ""}`}>{doc?.status || "N/A"}</span>
      </div>

      {error && <div className={styles.errorBanner}>{error}</div>}

      <button
        type="button"
        className={styles.openBtn}
        onClick={onOpen}
        disabled={!isOpenButtonEnabled}
        aria-label="Open document in Word"
      >
        {isOpeningInWord ? (
          <>
            <span className={styles.spinnerSm} aria-hidden="true" />{" "}
            Opening...
          </>
        ) : (
          "Open"
        )}
      </button>
    </div>
  );
}
